import { ensureAuthSession, hasStoredAuthSession } from './authApi';
import { getEnv } from '../utils/env';
import { API_CONNECTION_FAILED } from './apiConnectionMonitor';
import {
  notifyStreamEvent,
  type MatchStreamEventName,
  type MatchStreamPayload,
  type NotifyStreamEventOptions,
} from './sseNotifications';

export type StreamScope = 'global' | 'team' | 'club' | 'user';

export type StreamEvent = {
  name: MatchStreamEventName;
  payload: MatchStreamPayload;
};

export const extractPresenceUserCount = (payload: MatchStreamPayload): number | null => {
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  const candidates = [record.liveUsers, record.onlineUsers, record.userCount, record.count];

  for (const value of candidates) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }

  return null;
};

export type StreamSubscriptionOptions = NotifyStreamEventOptions & {
  monitorConnection?: boolean;
  enabled?: boolean;
  notify?: boolean;
  onEvent?: (event: StreamEvent) => void;
  onOpen?: () => void;
  onError?: (error: Event) => void;
};

type StreamSubscription = {
  close: () => void;
};

const SUPPORTED_EVENTS: MatchStreamEventName[] = [
  'heartbeat',
  'server.stopping',
  'connected',
  'match.updated',
  'match.deleted',
  'live_event.created',
  'live_event.updated',
  'live_event.deleted',
  'live_events.reset',
  'roadmap.updated',
  'presence.updated',
];

const getApiBaseUrl = (): string => getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');

const getApiKey = (): string => getEnv('VITE_AI_API_KEY', '');

const parseEventPayload = (raw: string): MatchStreamPayload => {
  if (!raw || raw.trim().length === 0) return null;
  try {
    return JSON.parse(raw) as MatchStreamPayload;
  } catch {
    return { message: raw };
  }
};

const buildStreamUrl = (path: string, monitorConnection = false): string => {
  const baseUrl = getApiBaseUrl();
  const apiKey = getApiKey();
  if (!baseUrl) {
    throw new Error('VITE_AI_API_URL is not configured');
  }
  if (!apiKey) {
    throw new Error('VITE_AI_API_KEY is not configured');
  }

  const url = new URL(`${baseUrl}${path}`);
  if (!url.searchParams.has('api_key')) {
    url.searchParams.set('api_key', apiKey);
  }
  if (monitorConnection) url.searchParams.set('monitor_connection', 'true');

  return url.toString();
};

const subscribeToUrl = (
  url: string,
  options: StreamSubscriptionOptions = {},
  authenticated = true,
): StreamSubscription => {
  if (typeof window === 'undefined' || typeof EventSource === 'undefined' || options.enabled === false) {
    return { close: () => undefined };
  }

  let source: EventSource | null = null;
  let stopped = false;
  let attempt = 0;
  let heartbeatSupported = false;
  let heartbeatTimeout = 6000;
  let watchdog: ReturnType<typeof setTimeout> | undefined;
  let retry: ReturnType<typeof setTimeout> | undefined;

  const disconnect = () => {
    attempt += 1;
    clearTimeout(watchdog);
    clearTimeout(retry);
    source?.close();
    source = null;
  };
  const failed = (error: Event) => {
    if (stopped) return;
    options.onError?.(error);
    if (!options.monitorConnection && !authenticated) return; // Public streams retain native retries.
    disconnect();
    if (navigator.onLine !== false) retry = setTimeout(connect, 5000);
  };
  const armWatchdog = (timeout = heartbeatTimeout) => {
    clearTimeout(watchdog);
    if (options.monitorConnection) {
      watchdog = setTimeout(() => failed(new Event('timeout')), timeout);
    }
  };
  const connect = () => {
    if (stopped) return;
    disconnect();
    if ((options.monitorConnection || authenticated) && navigator.onLine === false) {
      options.onError?.(new Event('offline'));
      return;
    }
    const currentAttempt = attempt;
    const open = () => {
      if (stopped || currentAttempt !== attempt) return;
      heartbeatSupported = false;
      const current = new EventSource(url, { withCredentials: true });
      source = current;
      armWatchdog(6000); // Also detect a request that never opens.
      SUPPORTED_EVENTS.forEach((eventName) => {
        current.addEventListener(eventName, (event: Event) => {
          if (stopped || source !== current) return;
          const payload = parseEventPayload((event as MessageEvent<string>).data);
          if (eventName === 'connected') {
            const interval = payload && 'heartbeatIntervalSeconds' in payload ? payload.heartbeatIntervalSeconds : null;
            heartbeatSupported = typeof interval === 'number' && Number.isFinite(interval) && interval >= 1 && interval <= 60;
            if (heartbeatSupported) {
              // Four seconds of jitter tolerance; the two-second app heartbeat
              // therefore detects silent failures after six seconds without data.
              heartbeatTimeout = Math.max(6000, Number(interval) * 1000 + 4000);
            }
            // Allow the frontend to be deployed before the heartbeat-enabled API.
            if (!heartbeatSupported) clearTimeout(watchdog);
          }
          if (heartbeatSupported) armWatchdog();
          if (eventName === 'heartbeat') return;
          if (options.notify !== false) notifyStreamEvent(eventName, payload, options);
          options.onEvent?.({ name: eventName, payload });
        });
      });
      current.onopen = () => {
        if (!stopped && source === current) options.onOpen?.();
      };
      current.onerror = (error) => {
        if (!stopped && source === current) failed(error);
      };
    };
    if (authenticated && hasStoredAuthSession()) {
      void ensureAuthSession().then(open).catch(() => {
        if (!stopped && currentAttempt === attempt) failed(new Event('authentication-unavailable'));
      });
    } else open();
  };
  const offline = () => failed(new Event('offline'));
  const online = () => connect();
  const apiFailed = () => {
    // A failed request invalidates the apparently healthy stream too. Further
    // failed requests must not postpone an already scheduled reconnect.
    if (source) failed(new Event(API_CONNECTION_FAILED));
  };
  const resume = () => {
    if (typeof document === 'undefined' || document.visibilityState !== 'hidden') connect();
  };
  if (authenticated) {
    window.addEventListener('pageshow', resume);
    window.addEventListener('focus', resume);
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', resume);
  }
  if (options.monitorConnection || authenticated) {
    window.addEventListener('offline', offline);
    window.addEventListener('online', online);
    window.addEventListener(API_CONNECTION_FAILED, apiFailed);
  }
  connect();

  return {
    close: () => {
      stopped = true;
      disconnect();
      window.removeEventListener('pageshow', resume);
      window.removeEventListener('focus', resume);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('offline', offline);
      window.removeEventListener('online', online);
      window.removeEventListener(API_CONNECTION_FAILED, apiFailed);
    },
  };
};

export const subscribeToMatchStream = (
  params: { matchId?: string | null; matchKey?: string | null },
  options: StreamSubscriptionOptions = {}
): StreamSubscription => {
  const trimmedMatchId = typeof params.matchId === 'string' ? params.matchId.trim() : '';
  const trimmedMatchKey = typeof params.matchKey === 'string' ? params.matchKey.trim() : '';

  if (!trimmedMatchId && !trimmedMatchKey) {
    return { close: () => undefined };
  }

  const path = trimmedMatchId
    ? `/api/v1/live/matches/${encodeURIComponent(trimmedMatchId)}/stream`
    : `/api/v1/live/matches/key/${encodeURIComponent(trimmedMatchKey)}/stream`;

  return subscribeToUrl(buildStreamUrl(path), options);
};

export const subscribeToPublicMatchStream = (
  matchKey: string,
  options: StreamSubscriptionOptions = {}
): StreamSubscription => {
  const trimmedMatchKey = matchKey.trim();
  if (!trimmedMatchKey) return { close: () => undefined };
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) throw new Error('VITE_AI_API_URL is not configured');
  return subscribeToUrl(
    `${baseUrl}/api/v1/public/live/matches/${encodeURIComponent(trimmedMatchKey)}/stream`,
    { ...options, notify: false },
    false,
  );
};

export const subscribeToGlobalChannelStream = (
  channel: string,
  options: StreamSubscriptionOptions = {}
): StreamSubscription => {
  const trimmedChannel = channel.trim();
  if (!trimmedChannel) {
    return { close: () => undefined };
  }

  const path = `/api/v1/stream/global?channel=${encodeURIComponent(trimmedChannel)}`;
  return subscribeToUrl(buildStreamUrl(path, options.monitorConnection), options);
};

export const subscribeToScopedStream = (
  scope: StreamScope,
  identifier: string,
  options: StreamSubscriptionOptions = {}
): StreamSubscription => {
  const trimmedIdentifier = identifier.trim();
  if (!trimmedIdentifier) {
    return { close: () => undefined };
  }

  const path = `/api/v1/stream/${encodeURIComponent(scope)}/${encodeURIComponent(trimmedIdentifier)}`;
  return subscribeToUrl(buildStreamUrl(path, options.monitorConnection), options);
};
